// Approvers' worklist for salary splits (Increment B). The server enforces
// segregation of duties (not the proposer, not the employee themselves) and
// re-checks every destination at approval — its message is shown verbatim.
import React from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { SplitSquareHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DataState } from '@/features/shared/components/ui/DataState';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { usePendingSalaryAllocations, useDecideSalaryAllocation, type AllocationPlan } from '../api/salaryAllocationApi';
import { bpToPercent } from '../allocationModel';

export const PendingSalarySplits: React.FC<{ nameOf: (id?: string | null) => string }> = ({ nameOf }) => {
  const { t } = useTranslation('payroll');
  const prompt = usePrompt();
  const q = usePendingSalaryAllocations();
  const decide = useDecideSalaryAllocation();

  const act = async (p: AllocationPlan, action: 'approve' | 'reject') => {
    let reason: string | undefined;
    if (action === 'reject') {
      const r = await prompt({
        title: t('salarySplit.rejectTitle', { defaultValue: 'Reject this salary split?' }),
        label: t('salarySplit.reason', { defaultValue: 'Reason' }),
        required: true, multiline: true, minLength: 3, danger: true,
        confirmLabel: t('salarySplit.reject', { defaultValue: 'Reject' }),
      });
      if (r === null) return;
      reason = r.trim();
    }
    try {
      await decide.mutateAsync({ id: p.id, action, reason });
      toast.success(action === 'approve'
        ? t('salarySplit.approved', { defaultValue: 'Salary split approved.' })
        : t('salarySplit.rejected', { defaultValue: 'Salary split rejected.' }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('salarySplit.actionFailed', { defaultValue: 'That did not go through.' })));
    }
  };

  const plans = q.data ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <SplitSquareHorizontal className="h-5 w-5 text-muted-foreground" aria-hidden />
          {t('salarySplit.pendingListTitle', { defaultValue: 'Salary splits awaiting approval' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <DataState variant="loading" compact />
        ) : q.isError ? (
          <DataState
            variant="error"
            compact
            message={getApiErrorMessage(q.error, t('salarySplit.pendingLoadFailed', { defaultValue: 'Could not load salary splits awaiting approval.' }))}
            onRetry={() => void q.refetch()}
          />
        ) : plans.length === 0 ? (
          <DataState variant="empty" compact message={t('salarySplit.pendingEmpty', { defaultValue: 'No salary splits awaiting approval.' })} />
        ) : (
          <ul className="divide-y divide-border">
            {plans.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{nameOf(p.employee_id)}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.lines.length === 0
                      ? t('salarySplit.backToDefault', { defaultValue: '100% to the default payment method' })
                      : p.lines.map((l) => `${l.masked} ${bpToPercent(l.percent_bp)}%`).join(' · ')}
                    {' — '}
                    {t('salarySplit.fromDate', { defaultValue: 'From {{date}}', date: p.effective_from.slice(0, 10) })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={decide.isPending} onClick={() => void act(p, 'approve')}>
                    {t('salarySplit.approve', { defaultValue: 'Approve' })}
                  </Button>
                  <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => void act(p, 'reject')}>
                    {t('salarySplit.reject', { defaultValue: 'Reject' })}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingSalarySplits;
